import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Hooks
import { useReturnTo } from './useReturnTo';

// Api
import api from '../api/Base';
import archiver from '../api/Archiver';

// Types
import Action from '../types/Action';
import AlertRules from '../types/Alerts/AlertRules';
import InstanceTarget from '../types/instance/InstanceTarget';
import HostTarget from '../types/host/HostTarget';
import {
    AlertRuleFormData,
    AlertRuleFormState,
    AlertRuleFormValidation,
    createDefaultFormData,
    createDefaultValidation,
    createDefaultUIState,
    createDefaultDependencies
} from '../types/Alerts/AlertRuleFormState';

// Utils
import { AlertRuleFormValidator, AlertRuleFormUtils } from '../helpers/AlertRuleFormValidation';
import { archiverUrl } from '../helpers/utils';
import { showError } from '../utils/showAlert';

// Constants
import { ACTIONS, DATA_LOADED } from '../component/Constants';

interface UseAlertRuleFormProps {
    alertRules: AlertRules[];
    instances: InstanceTarget[];
    hosts: HostTarget[];
    loading: number;
    refresh: Function;
}

export const useAlertRuleForm = ({
    alertRules,
    instances,
    hosts,
    loading,
    refresh
}: UseAlertRuleFormProps) => {
    const returnFromForm = useReturnTo('/admin/alert-rules');
    const { actionId, alertRuleId } = useParams();

    const [formState, setFormState] = useState<AlertRuleFormState>({
        formData: createDefaultFormData(),
        validation: createDefaultValidation(),
        uiState: createDefaultUIState(actionId === ACTIONS.DELETE.type),
        dependencies: createDefaultDependencies()
    });

    const [action, setAction] = useState<Action>(ACTIONS.CREATE);

    const updateFormData = useCallback((updates: Partial<AlertRuleFormData>) => {
        setFormState(prev => ({
            ...prev,
            formData: { ...prev.formData, ...updates }
        }));
    }, []);

    const updateValidation = useCallback((updates: Partial<AlertRuleFormValidation>) => {
        setFormState(prev => ({
            ...prev,
            validation: { ...prev.validation, ...updates }
        }));
    }, []);

    const updateUIState = useCallback((updates: Partial<typeof formState.uiState>) => {
        setFormState(prev => ({
            ...prev,
            uiState: { ...prev.uiState, ...updates }
        }));
    }, []);

    const applyRuleToForm = useCallback((rule: AlertRules) => {
        updateFormData({
            id: rule.id,
            name: rule.name,
            disabled: rule.disabled,
            decreasing: rule.decreasing ? 'true' : 'false',
            entitytype: rule.entitytype,
            metrictype: rule.metrictype,
            aggregation: rule.aggregation,
            statistic: rule.statistic,
            intervalminutes: rule.intervalminutes ?? 1,
            evaluationwindowminutes: rule.evaluationwindowminutes ?? 1,
            units: rule.units,
            thresholdarray: [1, 2, 3].map(sev => {
                const existing = rule.thresholdarray?.find(t => t.severity === sev);
                return existing ? { ...existing } : { threshold: NaN, severity: sev };
            }),
            properties: (rule.properties ?? []).map(p => ({ ...p })),
            host: [...(rule.host ?? [])],
            datasource: [...(rule.datasource ?? [])],
            tag: (rule.tag ?? []).map(t => ({ ...t })),
            disk: [...(rule.disk ?? [])],
            availabilitygroup: [...(rule.availabilitygroup ?? [])],
            anyavailabilitygroup: rule.anyavailabilitygroup ?? true,
            ...AlertRuleFormUtils.resolveAnyFlags(rule)
        });
    }, [updateFormData]);

    // Falls back to a targeted v2 lookup so deep links (e.g. from alert
    // emails) resolve when the rule isn't in the locally cached list.
    const loadAlertRule = useCallback(async (alertRuleId: string) => {
        const localRule = alertRules.find(r => parseInt(r.id) === Number(alertRuleId));

        if (localRule) {
            applyRuleToForm(localRule);
            return;
        }

        try {
            const response = await archiver.get(`/rest/v2/alert/rule?id=${encodeURIComponent(alertRuleId)}`);
            const data = response?.data;
            const remoteRule: AlertRules | undefined = Array.isArray(data) ? data[0] : data;

            if (remoteRule && remoteRule.id !== undefined && remoteRule.id !== null) {
                applyRuleToForm(remoteRule);
                return;
            }
        } catch (error) {
            console.error('Failed to retrieve alert rule by id.', error);
        }

        updateUIState({ isMissing: true });
    }, [alertRules, applyRuleToForm, updateUIState]);

    const setActionFromId = useCallback(() => {
        switch (actionId) {
            case 'create':
                setAction(ACTIONS.CREATE);
                break;
            case 'edit':
                setAction(ACTIONS.UPDATE);
                break;
            case 'delete':
                setAction(ACTIONS.DELETE);
                updateUIState({ disabled: true });
                break;
            default:
                console.error('Unknown action requested', actionId);
                updateUIState({ isError: true });
                break;
        }
    }, [actionId, updateUIState]);

    const validateName = useCallback(() => {
        const validation = AlertRuleFormValidator.validateName(
            formState.formData.name,
            formState.formData.id,
            alertRules
        );

        updateValidation({ name: validation });
        updateFormData({ name: formState.formData.name.trim() });
    }, [formState.formData.name, formState.formData.id, alertRules, updateValidation, updateFormData]);

    const validateThresholdArray = useCallback(() => {
        const validation = AlertRuleFormValidator.validateThresholdArray(
            formState.formData.thresholdarray,
            formState.formData.units,
            formState.formData.decreasing
        );
        updateValidation({ thresholdarray: validation });
    }, [formState.formData.thresholdarray, formState.formData.units, formState.formData.decreasing, updateValidation]);

    const validateEvaluationWindow = useCallback(() => {
        const validation = AlertRuleFormValidator.validateEvaluationWindow(
            formState.formData.evaluationwindowminutes
        );
        updateValidation({ evaluationwindow: validation });
    }, [formState.formData.evaluationwindowminutes, updateValidation]);

    const validateStatistic = useCallback(() => {
        // Condition section is hidden for *_status types — skip metric validation in that case.
        if (formState.formData.metrictype === 'status') {
            updateValidation({ statistic: { isValid: true, hasError: false } });
            return true;
        }
        const validation = AlertRuleFormValidator.validateStatistic(formState.formData.statistic);
        updateValidation({ statistic: validation });
        return validation.isValid;
    }, [formState.formData.statistic, formState.formData.metrictype, updateValidation]);

    const resetFieldsForType = useCallback((typeValue: string) => {
        const entityType = AlertRuleFormUtils.getEntityOrMetric('entity', typeValue);
        const metricType = AlertRuleFormUtils.getEntityOrMetric('metric', typeValue);

        updateFormData({
            entitytype: entityType,
            metrictype: metricType,
            host: [],
            datasource: [],
            tag: [],
            disk: [],
            anyhost: true,
            anydatasource: true,
            anydisk: true,
            availabilitygroup: [],
            anyavailabilitygroup: true,
            // *_status alerts hide the Condition section; backend still requires a
            // non-empty statistic, and for these rules it must be 'sensorstatus' (not 'status')
            // for the value to be found at evaluation time.
            statistic: metricType === 'status' ? 'sensorstatus' : ''
        });
    }, [updateFormData]);

    const handleSubmit = useCallback(async (event?: React.FormEvent) => {
        if (event) {
            event.preventDefault();
        }

        updateUIState({ loading: true });

        const isStatisticValid = validateStatistic();

        const { isValid, validation } = AlertRuleFormValidator.validateCompleteForm(
            formState.formData,
            alertRules
        );

        updateValidation(validation);

        if (!isValid || !isStatisticValid) {
            updateUIState({ loading: false });
            return;
        }

        try {
            const fd = formState.formData;
            const serverData = [{
                id: fd.id,
                name: fd.name,
                entitytype: fd.entitytype,
                metrictype: fd.metrictype,
                intervalminutes: fd.intervalminutes,
                evaluationwindowminutes: fd.evaluationwindowminutes,
                units: fd.units,
                decreasing: fd.decreasing === 'true',
                disabled: fd.disabled,
                aggregation: fd.aggregation,
                // Status alerts hide the metric field but backend requires non-empty statistic.
                statistic: fd.metrictype === 'status' && !fd.statistic ? 'sensorstatus' : fd.statistic,
                thresholdarray: fd.thresholdarray.filter(t => !isNaN(t.threshold)),
                properties: fd.properties,
                host: fd.host,
                datasource: fd.datasource,
                tag: fd.tag,
                disk: fd.disk,
                anyhost: fd.anyhost,
                anydatasource: fd.anydatasource,
                anydisk: fd.anydisk,
                availabilitygroup: fd.availabilitygroup,
                anyavailabilitygroup: fd.anyavailabilitygroup
            }];

            let result;
            const url = `${archiverUrl(2)}/alert/rule`;
            const config = { baseURL: '' };

            switch (action.type) {
                case 'create':
                    result = await api.post(url, serverData, config);
                    break;
                case 'update':
                    result = await api.put(url, serverData, config);
                    break;
                case 'delete':
                    result = await api.delete(url, { ...config, data: serverData });
                    break;
                default:
                    console.error('Unknown action requested', action);
                    updateUIState({ isError: true, loading: false });
                    return;
            }

            if (result && result.status === 200) {
                returnFromForm();
                refresh();
            } else {
                updateUIState({ isError: true, loading: false });
            }

        } catch (error: any) {
            console.error('Failed to complete requested action', action, error);
            const errMsg = error.response?.data?.message || error.message || error.toString();
            const errorHeading = `Failed to ${action.title.toLowerCase()} alert rule`;
            showError(errorHeading, errMsg);
            updateUIState({ isError: true, loading: false });
        }
    }, [formState, alertRules, action, returnFromForm, refresh, updateUIState, updateValidation, validateStatistic]);

    // Wait until the global data load finishes before resolving the rule —
    // a not-yet-populated local list would otherwise trip the API fallback
    // or the missing-rule state prematurely.
    useEffect(() => {
        setActionFromId();

        if (alertRuleId && loading === DATA_LOADED) {
            loadAlertRule(alertRuleId);
        }
    }, [alertRuleId, alertRules, actionId, loading]);

    return {
        formState,
        action,
        updateFormData,
        updateValidation,
        updateUIState,
        validateName,
        validateThresholdArray,
        validateEvaluationWindow,
        validateStatistic,
        handleSubmit,
        resetFieldsForType,
        sortedInstances: instances.sort((a, b) => a.name.localeCompare(b.name)),
        sortedHosts: hosts.sort((a, b) => a.name.localeCompare(b.name))
    };
};
